import type {} from "@mui/x-date-pickers/themeAugmentation";
import { createTheme } from "@mui/material/styles";
import { readableForeground } from "./contrast";
import { darkTokens, lightTokens, fontStack, shapeTokens } from "./tokens";
import { layoutOverrides } from './layoutOverrides';

const phoneMedia = createTheme().breakpoints.down('sm');

export const createAppTheme = (resolvedMode: "light" | "dark") => {
  const tokens = resolvedMode === "light" ? lightTokens : darkTokens;
  const { surfaces, text, status, statusText } = tokens;
  const colors = {
    primary: statusText.neutral,
    secondary: statusText.inFlight,
    error: statusText.attention,
    warning: statusText.inFlight,
    success: statusText.ready,
    info: statusText.settled,
  };
  const controlBorder = surfaces.controlBorder;
  const fillPalette = (main: string) => ({
    main,
    contrastText: readableForeground(main),
    light: surfaces.surfaceRaised,
    dark: main,
  });
  const theme = createTheme({
    palette: {
      mode: resolvedMode,
      // primary must stay safe as a BACKGROUND: roughly a dozen components
      // use `primary.main` as a fill behind text (avatars, notification dots,
      // chips), so a near-white primary makes their content invisible. The
      // walkthrough's white primary button is applied via the
      // MuiButton.containedPrimary override below instead.
      //
      // surfaceRaised is also what the walkthrough uses for its circular icon
      // wells, so avatars land on the intended colour.
      // light/dark are set explicitly rather than left to MUI's tonal
      // derivation. Derived from a grey main, primary.light landed on a
      // mid-grey that several components use as a panel background, where
      // neither dark nor secondary text could reach 4.5:1. In dark mode
      // primary.light stays a dark raised surface; in light mode it becomes a
      // pale tint, which is what a highlight panel wants in each case.
      primary: {
        main: resolvedMode === "light" ? "#5A6169" : surfaces.surfaceRaised,
        light: resolvedMode === "light" ? "#E4E7EB" : "#474D55",
        dark: resolvedMode === "light" ? "#3C4249" : "#23272C",
        contrastText: "#FFFFFF",
      },
      secondary: fillPalette(status.inFlight),
      error: fillPalette(status.attention),
      warning: fillPalette(status.inFlight),
      success: fillPalette(status.ready),
      info: fillPalette(status.settled),
      background: { default: surfaces.canvas, paper: surfaces.surface },
      divider: surfaces.border,
      text: {
        primary: text.primary,
        secondary: text.secondary,
        disabled: text.disabled,
      },
      status,
      statusText,
      readable: text,
      contrastThreshold: 4.5,
      action: {
        active: text.secondary,
        disabled: text.disabled,
        disabledOpacity: 1,
      },
      surfaces,
    },
    shape: { borderRadius: shapeTokens.borderRadius },
    typography: {
      fontFamily: fontStack,
      h3: { [phoneMedia]: { fontSize: '2rem', lineHeight: 1.2 } },
      h4: { [phoneMedia]: { fontSize: '1.75rem', lineHeight: 1.25 } },
    },
    components: {
      MuiFormControlLabel: {
        styleOverrides: { root: { [phoneMedia]: { minHeight: 44 } } },
      },
      MuiMenuItem: {
        styleOverrides: { root: { [phoneMedia]: { minHeight: 44, whiteSpace: 'normal' } } },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            [phoneMedia]: {
              width: 308,
              maxWidth: '100vw',
              '& .MuiDayCalendar-weekContainer': { margin: 0 },
              '& .MuiDayCalendar-weekDayLabel': { width: 44, margin: 0 },
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            [phoneMedia]: { width: 44, height: 44, margin: 0 },
            "&.Mui-selected, &.MuiPickersDay-today": {
              border: `1px solid ${text.link}`,
            },
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            color:
              ownerState.color === "inherit"
                ? "inherit"
                : colors[ownerState.color as keyof typeof colors] || text.link,
          }),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: surfaces.surfaceRaised },
          bar: ({ ownerState }) => ({
            backgroundColor:
              colors[ownerState.color as keyof typeof colors] || text.link,
          }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: surfaces.surfaceRaised,
            color: text.primary,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { "&::placeholder": { color: text.secondary, opacity: 1 } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            [phoneMedia]: { minWidth: 44, minHeight: 44 },
            color:
              ownerState.color === "inherit"
                ? "inherit"
                : colors[ownerState.color as keyof typeof colors] ||
                  text.secondary,
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: text.secondary,
            "&.Mui-selected": { color: text.link },
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: { indicator: { backgroundColor: text.link } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: text.primary,
            borderColor: controlBorder,
            "&.Mui-selected, &.Mui-selected:hover": {
              color: text.link,
              borderColor: text.link,
              backgroundColor: surfaces.inset,
            },
            "&.Mui-disabled, &.Mui-selected.Mui-disabled": {
              color: text.disabled,
              borderColor: surfaces.border,
            },
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: { backgroundColor: controlBorder, opacity: 1 },
          thumb: {
            backgroundColor: resolvedMode === "light" ? "#FFFFFF" : "#000000",
          },
          switchBase: {
            "&.Mui-focusVisible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: -2,
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: text.link,
              opacity: 1,
            },
            "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const color = ownerState.color;
            return color && color in colors
              ? { color: colors[color as keyof typeof colors] }
              : {};
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            [phoneMedia]: { '&:is(button)': { minHeight: 44, minWidth: 44 } },
            color: text.link,
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color &&
              ownerState.color in colors && {
                color: colors[ownerState.color as keyof typeof colors],
              }),
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: controlBorder },
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: text.secondary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: text.link,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.error,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Keep foreground/background changes paired when switching themes.
            transition: "transform 200ms",
            "&.Mui-focused": { color: text.link },
            "&.Mui-error": { color: colors.error },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { "&.Mui-error": { color: colors.error } } },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            [phoneMedia]: { minWidth: 44, minHeight: 44 },
            color: controlBorder,
            "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: text.link },
            "&.Mui-focusVisible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            [phoneMedia]: { minWidth: 44, minHeight: 44 },
            color: controlBorder,
            "&.Mui-checked": { color: text.link },
            "&.Mui-focusVisible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const color = ownerState.severity || "info";
            return ownerState.variant === "filled"
              ? {}
              : {
                  backgroundColor: surfaces.surfaceRaised,
                  color: colors[color],
                  "& .MuiAlert-icon": { color: colors[color] },
                };
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: surfaces.surface,
            backgroundImage: "none",
            color: text.primary,
            borderBottom: `1px solid ${surfaces.border}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${surfaces.border}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            [phoneMedia]: { minWidth: 44, minHeight: 44 },
            textTransform: "none",
            fontWeight: 600,
            borderRadius: shapeTokens.buttonBorderRadius,
            transition: "box-shadow 150ms",
            ...(ownerState.variant !== "contained" &&
              ownerState.color !== "inherit" && {
                color:
                  colors[ownerState.color as keyof typeof colors] ||
                  text.primary,
              }),
            "&:focus-visible": {
              outline: `2px solid ${text.link}`,
              outlineOffset: 2,
            },
          }),
          outlined: { borderColor: controlBorder },
          // Primary actions in the walkthrough are plain high-contrast blocks,
          // never a brand colour. Scoped to containedPrimary so that
          // contained error/warning/success buttons keep their own colour.
          containedPrimary: {
            backgroundColor: status.neutral,
            color: resolvedMode === "light" ? "#FFFFFF" : surfaces.inset,
            "&:hover": {
              backgroundColor: resolvedMode === "light" ? "#2A2F35" : "#E4E6E9",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            height: 'auto',
            minHeight: ownerState.size === 'small' ? 24 : 32,
            maxWidth: '100%',
            '& .MuiChip-label': {
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              paddingTop: ownerState.size === 'small' ? 2 : 4,
              paddingBottom: ownerState.size === 'small' ? 2 : 4,
            },
            ...((ownerState.onDelete || ownerState.clickable) && {
              [phoneMedia]: {
                minHeight: 44,
                '& .MuiChip-deleteIcon': { width: 44, height: 44, padding: 12, boxSizing: 'border-box', margin: 0 },
              },
            }),
            borderRadius: 0,
            transition: "box-shadow 150ms",
            ...(ownerState.variant !== "outlined" && {
              "& .MuiChip-icon": { color: "inherit" },
            }),
            ...(ownerState.variant === "outlined" && {
              color:
                colors[ownerState.color as keyof typeof colors] || text.primary,
              borderColor: controlBorder,
            }),
          }),
        },
      },
    },
  });
  return createTheme(theme, { components: layoutOverrides(theme) });
};
